<template>
  <div
    v-if="pageLoading"
    class="m-3"
  >
    <placeholder-content :is-load="true" />
  </div>
  <div
    v-else-if="!hasPermission"
    class="m-3"
  >
    <access-denied/>
  </div>
  <div
    v-else
  >
    <div class="d-flex justify-content-between border-bottom mb-3">
      <ul class="nav nav-custom">
        <li class="nav-item">
          <a class="nav-link active">
            {{ $t('All') }}
            <span class="badge rounded-pill bg-primary">
              {{ datatable.total }}
            </span>
          </a>
        </li>
        <!-- <li class="nav-item">
          <a class="nav-link disabled" tabindex="-1" aria-disabled="true">Disabled</a>
        </li> -->
      </ul>

      <column-toggle
        :headers="allHeaders"
        storage-key="inventories.columns"
        v-model="visibleColumns"
      />
    </div>
    <div class="d-flex justify-content-start mx-3 flex-column flex-sm-row">
      <div class="btn-group btn-group-sm me-sm-2 mb-3">
        <button
          type="button"
          class="btn btn-sm btn-primary"
          @click="() => {
            form.open = true;
            form.type = 'CREATE';
          }"
        >
          <i class="fa-solid fa-circle-plus me-2" />
          {{ $t('Create') }}
        </button>
      </div>

      <div class="custom-input-group input-group input-group-sm me-sm-2 mb-3 w-auto">
        <input
          type="search"
          class="form-control"
          :placeholder="$t('search')+'...'"
          aria-label="Search"
          aria-describedby="search-addon"
          v-model.trim="params.search"
          @keyup.enter="fetchData"
        >
        <button
          class="input-group-text"
          type="button"
          @click="fetchData"
        >
          <i class="fas fa-search" />
        </button>
      </div>

      <button
        type="button"
        class="btn btn-sm btn-outline-secondary text-nowrap me-sm-2 mb-3"
        @click="offcanvasFilter.open = true"
      >
        <i class="fa-solid fa-filter me-1" />
        {{ $t('Filters') }}
        <span v-if="activeFilterCount" class="badge bg-primary ms-1 rounded-pill">
          {{ activeFilterCount }}
        </span>
      </button>

      <per-page
        @per-page="setPerPage"
      />
    </div>
    <vue3-easy-data-table
      v-model:server-options="datatable.serverOptions"
      ref="dataTable"
      :headers="visibleHeaders"
      :items="datatable.items"
      :loading="datatable.loading"
      :server-items-length="datatable.total"
      :theme-color="'#515151'"
      :hide-footer="false"
      :hide-rows-per-page="true"
      :empty-message="$t('No Available Data')"
      :table-min-height="null"
      :body-item-class-name="'align-top py-1 px-2'"
      buttons-pagination
      table-class-name='customize-table'
      header-text-direction="center"
    >
      <template #item-image="item">
        <div class="text-center">
          <img
            v-if="item.thumbnail_url && item.image_url"
            :src="item.thumbnail_url"
            class="img-table"
            @click="showImage(item.image_url)"
            style="cursor: pointer;"
          >
          <img
            v-else
            :src="$storeIndex.getImageSrc('')"
            width="25px"
            height="25px"
          >
        </div>
      </template>
      <template #item-type="item">
        <span
          class='badge'
          :style="$storeIndex.backgroundColorInventoryType(item.type)"
        >
          {{ $t(item.type) }}
        </span>
      </template>
      <template #item-category="item">
        {{ $t(item.category) }}
      </template>
      <template #item-out_of_stock_branches="item">
        <div
          v-if="item.out_of_stock_branches && item.out_of_stock_branches.length"
          class="d-flex flex-wrap gap-1"
        >
          <span
            v-for="branch in item.out_of_stock_branches"
            :key="branch.branch_id"
            class="badge bg-danger-subtle text-danger border border-danger-subtle"
          >
            <i class="fa-solid fa-store me-1" />
            {{ branch.name }}
          </span>
        </div>
        <div
          v-else
          class="text-center text-muted"
        >
          —
        </div>
      </template>
      <template #item-quantity="item">
        <div class="text-end">
          {{ formatNumber(item.quantity) }}
        </div>
      </template>
      <template #item-cost="item">
        <div class="text-end">
          {{ item.cost_manual? formatNumberDigit(item.cost_manual): formatNumberDigit(item.cost) }}
        </div>
      </template>
      <template #item-unit="item">
        <div class="text-end">
          {{ $t(item.unit) }}
        </div>
      </template>
      <template #item-created_at="item">
        <div class="text-center">
          {{ formatDateTime(item.created_at) }}
        </div>
      </template>
      <template #item-created_by="item">
        <div class="text-end">
          {{ item.created_by }}
        </div>
      </template>
      <template #item-action="item">
        <div class="icon-link custom-actions">
          <popper
            :placement="'top'"
            :hover="true"
            :arrow="true"
            :disableClickAway="true"
            class="popper-default border-0 m-0"
          >
            <button
              type="button"
              data-bs-toggle="popover"
              data-bs-placement="top"
              data-bs-title="Custom popover"
              data-bs-content="This popover is themed via CSS variables."
              class="btn btn-outline-primary btn-sm btn-icon"
              @click="() => {
                formInfo.open = true;
                formInfo.inventoryId = item.id;
              }"
            >
              <i class="fa-solid far fa-eye" />
            </button>
            <template #content>
              <div>{{ $t('View') }}</div>
            </template>
          </popper>
          <popper
            :placement="'top'"
            :hover="true"
            :arrow="true"
            :disableClickAway="true"
            class="popper-default border-0 m-0"
          >
            <button
              type="button"
              data-bs-toggle="popover"
              data-bs-placement="top"
              data-bs-title="Custom popover"
              data-bs-content="This popover is themed via CSS variables."
              class="btn btn-outline-primary btn-sm btn-icon"
              @click="() => {
                form.open = true;
                form.inventoryId = item.id;
                form.type = 'EDIT';
              }"
            >
              <i class="fa-solid fa-square-pen" />
            </button>
            <template #content>
              <div>{{ $t('Edit') }}</div>
            </template>
          </popper>
          <popper
            :placement="'top'"
            :hover="true"
            :arrow="true"
            :disableClickAway="true"
            class="popper-default border-0 m-0"
          >
            <button
              type="button"
              class="btn btn-outline-primary btn-sm btn-icon"
              @click="() => {
                settingBranchForm.open = true;
                settingBranchForm.inventoryId = item.id;
              }"
            >
              <i class="fa-solid fa-code-branch" />
            </button>
            <template #content>
              <div>{{ $t('Setting Branches') }}</div>
            </template>
          </popper>
          <popper
            :placement="'top'"
            :hover="true"
            :arrow="true"
            :disableClickAway="true"
            class="popper-default border-0 m-0"
          >
            <button
              type="button"
              data-bs-toggle="popover"
              data-bs-placement="top"
              data-bs-title="Custom popover"
              data-bs-content="This popover is themed via CSS variables."
              class="btn btn-outline-primary btn-sm btn-icon"
              @click="handleDelete(item.id)"
            >
              <i class="fa-solid fa-trash-can" />
            </button>
            <template #content>
              <div>{{ $t('Delete') }}</div>
            </template>
          </popper>
          <!-- <button type="button" data-original-title="Delete" class="btn btn-outline-primary btn-sm btn-icon">
            <i class="fa-solid fa-trash-can" />
          </button> -->
        </div>
      </template>
      <template #loading>
        <i class="fa-solid fa-spinner fa-spin fa-2xl" />
      </template>
    </vue3-easy-data-table>
    <offcanvas-form
      v-if="form.open"
      :type="form.type"
      :inventory-id="form.inventoryId"
    />
    <offcanvas-info
      v-if="formInfo.open"
      :inventory-id="formInfo.inventoryId"
    />
    <offcanvas-setting-branch
      v-if="settingBranchForm.open"
      :inventory-id="settingBranchForm.inventoryId"
      @close="settingBranchForm.open = false"
    />
    <offcanvas-filter
      v-if="offcanvasFilter.open"
      :sections="filterSections"
      :value="{ category_ids: params.category_ids, types: params.types, branch_ids: params.branch_ids, stock_status: params.stock_status }"
      @apply="onApplyFilter"
      @close="offcanvasFilter.open = false"
    />
    <div
      class="modal fade"
      id="modalImage"
      ref="modalImage"
      aria-labelledby="modalImageLabel"
      aria-hidden="true"
    >
      <div class="modal-dialog modal-dialog-centered">
        <img
          :src="modalImage.image"
          class="d-block w-100"
          data-bs-dismiss="modal"
        >
      </div>
    </div>
  </div>
</template>

<script lang="js">
import Vue3EasyDataTable from 'vue3-easy-data-table';
import AccessDenied from "@/plugins/AccessDenied.vue";
import { Inventories } from '@/services/endpoint/inventories.service';
import FieldsDef from "./assets/template/datatable/FieldsDef";
import OffcanvasForm from './OffcanvasForm.vue';
import OffcanvasInfo from './OffcanvasInfo.vue';
import OffcanvasSettingBranch from './OffcanvasSettingBranch.vue';
import OffcanvasFilter from '@/components/shared/OffcanvasFilter.vue';
import ColumnToggle from '@/components/shared/ColumnToggle.vue';
import { loadVisibleColumns } from '@/utils/columnToggle';
import moment from "moment";
import PerPage from '@/plugins/PerPage.vue';
import PlaceholderContent from '@/plugins/PlaceholderContent.vue';
import Popper from "vue3-popper";
import numeral from 'numeral';

export default {
  data() {
    return {
      form: {
        open: false,
        type: 'CREATE',
        inventoryId: 0
      },
      formInfo: {
        open: false,
        inventoryId: 0,
      },
      settingBranchForm: {
        open: false,
        inventoryId: 0,
      },
      pageLoading: false,
      hasPermission: false,
      datatable: {
        loading: false,
        headers: FieldsDef,
        items: [],
        total: 0,
        serverOptions: {
          page: 1,
          rowsPerPage: 10
        }
      },
      params: {
        limit: 10,
        page: 1,
        search: '',
        category_ids: [],
        types: [],
        branch_ids: [],
        stock_status: []
      },
      filters: {
        categories: [],
        types: [],
        branches: []
      },
      offcanvasFilter: {
        open: false
      },
      modalImage: {
        open: false,
        image: ''
      },
      showCost: [],
      visibleColumns: loadVisibleColumns('inventories.columns', FieldsDef(t => t))
    }
  },
  components: {
    Vue3EasyDataTable,
    AccessDenied,
    OffcanvasForm,
    OffcanvasInfo,
    OffcanvasSettingBranch,
    OffcanvasFilter,
    ColumnToggle,
    PerPage,
    PlaceholderContent,
    Popper
  },
  computed: {
    activeFilterCount() {
      return this.params.category_ids.length + this.params.types.length + this.params.branch_ids.length + this.params.stock_status.length;
    },
    allHeaders() {
      return this.datatable.headers(this.$t);
    },
    visibleHeaders() {
      return this.allHeaders.filter(h => !h.togglable || this.visibleColumns.includes(h.value));
    },
    filterSections() {
      return [
        {
          key: 'branch_ids',
          label: 'Branch',
          icon: 'fa-store',
          options: this.filters.branches
        },
        {
          key: 'stock_status',
          label: 'Stock Status',
          icon: 'fa-box',
          single: true,
          options: [
            { id: 'out_of_stock', name: 'Out of Stock' },
            { id: 'in_stock', name: 'In Stock' }
          ]
        },
        {
          key: 'category_ids',
          label: 'Category',
          icon: 'fa-tag',
          options: this.filters.categories
        },
        {
          key: 'types',
          label: 'Type',
          icon: 'fa-layer-group',
          options: this.filters.types.map(t => ({ id: t.text, name: t.name }))
        }
      ];
    }
  },
  async mounted() {
    this.pageLoading = true;
    await this.fetchData();
    this.pageLoading = false;
  },
  methods: {
    async handleDelete(id) {
      try {
        let self = this;
        let title = this.$t("Delete Inventory") + '?';
        let html = this.$t("You will not be able to undo this action.");
        let confirmButtonText =
          '<i class="fas fa-check-circle me-1"></i>' + this.$t("Confirm");
        this.$swal({
          icon: "warning",
          iconColor: "#FF7043",
          title: title,
          // text: "You will not be able to undo this action.",
          html: html,
          showCancelButton: true,
          cancelButtonText: this.$t("Cancel"),
          confirmButtonText: confirmButtonText,
          buttonsStyling: false,
          customClass: {
            confirmButton: "btn btn-primary me-2",
            cancelButton: "btn btn-danger",
          },
          focusConfirm: false,
          showLoaderOnConfirm: true,
          preConfirm: async function () {
            return Inventories.delete(id)
              .then((response) => {
                self.fetchData();
                self.$toast.success("<B>" + self.$t('Success') + "</B>\n" + self.$t('Delete Product Success!'));
              })
              .catch((error) => {
                if (error?.response?.status === 403) {
                  self.$swal.update({
                    title: self.$t('Access Denied'),
                    confirmButtonText: '<i class="fas fa-redo me-1"></i>' + self.$t("Try again"),
                    text: self.$t('You do not have permission to access this page.')
                  });
                  self.$swal.showValidationMessage(
                    self.$t(error?.response?.data?.message)
                  )
                } else {
                  self.$toast.warn(error?.response?.data?.message);
                }
              })
          },
        });
      } catch (error) {
      }
    },
    async fetchData() {
      this.datatable.loading = true;
      const { stock_status, ...requestParams } = this.params;
      // BE expects `is_in_stock` as 1/0; only send it when a status is selected
      if (stock_status.length) {
        requestParams.is_in_stock = stock_status[0] === 'in_stock' ? 1 : 0;
      }
      var queryString = '?' + $.param(requestParams);
      await Inventories.list(queryString).then(res => {
        this.datatable.items = res.data.data;
        this.datatable.total = res.data.meta.total;
        this.filters.categories = res.data.filter?.categories || [];
        this.filters.types = res.data.filter?.types || [];
        this.filters.branches = res.data.filter?.branches || [];
        this.hasPermission = true;
      }).catch(err => {
        if (err?.response?.status === 403) {
          this.hasPermission = false;
        }
      });

      this.datatable.loading = false;
      return;
    },
    onApplyFilter(payload) {
      this.params.category_ids = payload.category_ids || [];
      this.params.types = payload.types || [];
      this.params.branch_ids = payload.branch_ids || [];
      this.params.stock_status = payload.stock_status || [];
      this.params.page = 1;
      this.datatable.serverOptions.page = 1;
      this.fetchData();
    },
    setPerPage(perPage) {
      this.params.limit = perPage;
      this.$refs.dataTable.updateRowsPerPageActiveOption(perPage);
      this.fetchData();
    },
    formatDateTime(value) {
      return moment(value).format('YYYY-MM-DD HH:mm:ss');
    },
    formatNumber(value) {
      return numeral(value).format("0,0");
    },
    formatNumberDigit(value) {
      return numeral(value).format("0,0.0000");
    },
    async showImage(image) {
      this.modalImage.image = image;
      $(this.$refs.modalImage).modal('show');
    }
  },
  watch: {
    "datatable.serverOptions" (nval, oval) {
      delete this.params.sort;
      this.params.page = nval.page;
      this.params.limit = nval.rowsPerPage;
      if (nval.sortBy && nval.sortType) this.params.sort = nval.sortBy + "|" + nval.sortType;
      this.fetchData();
    }
  }
}
</script>
